import { ReactNode } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * The parts of the viewer frame a caller can need without needing a viewer: the
 * size of the box, the empty states drawn in it, and the geometry types.
 *
 * Split out of DocumentViewerFrame because FileDisplay, ViewerErrorBoundary and
 * the page that hosts them are all in the main bundle and reach for these,
 * while DocumentViewerFrame pulls in ZoomToolbar — the MUI slider, menu and
 * icons that make up the zoom control — which only a mounted viewer has any use
 * for. Both viewers are lazily loaded (see FileDisplay); importing their chrome
 * eagerly would give most of that back.
 */

// Vertical breathing room inside the scroll container, and the gap between
// pages. Also subtracted from the height a fit-to-page render is measured
// against: clientHeight includes padding, so a page sized to the full
// clientHeight is taller than the space it has to sit in.
export const VIEWER_PADDING = 16;

/** How much of the window a viewer takes. Shared so the page that hosts one,
    and the lazy-loading placeholder that stands in for one, stay the same size
    as the viewer itself. */
export const VIEWER_HEIGHT = "80vh";

export type ViewerStatus = "loading" | "ready" | "error";

export type Size = { width: number; height: number };

export const ZERO_SIZE: Size = { width: 0, height: 0 };

/** Centred status text, sized to sit unobtrusively above a blank page area. */
export const ViewerMessage = ({ children }: { children: ReactNode }) => (
    <Typography variant="body2" align="center" sx={{ padding: "32px" }}>
        {children}
    </Typography>
);

export const frameSx = {
    width: "100%",
    maxWidth: "100vw",
    height: VIEWER_HEIGHT,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "background.paper",
    // The page lays this viewer out beside the info sidebar as a flex item,
    // whose default min-width:auto refuses to shrink below its content. Zooming
    // would then widen the viewer and push the sidebar out of the row entirely.
    // The zoomed document is the scroll container's problem, not the layout's.
    minWidth: 0,
} as const;

/** The frame with nothing in it. Declared once so that every state a reader can
    be left looking at is the same box, and none of them moves the page. */
const EmptyFrame = ({ children }: { children: ReactNode }) => (
    <Box sx={{ ...frameSx, alignItems: "center", justifyContent: "center" }}>{children}</Box>
);

/** For the moment before a viewer exists at all — a lazily-loaded chunk on its
    way. Same box, so there is no size jump when the real viewer takes over and
    draws its own loading state. */
export const ViewerPlaceholder = () => (
    <EmptyFrame>
        <CircularProgress />
    </EmptyFrame>
);

/** For a viewer that never arrived at all — a chunk that failed to load, which
    is not something the viewer itself can report because there is no viewer.
    See ViewerErrorBoundary. */
export const ViewerUnavailable = () => {
    const { t } = useTranslation();

    return (
        <EmptyFrame>
            <ViewerMessage>{t("document_preview_error")}</ViewerMessage>
        </EmptyFrame>
    );
};

/** The space inside a frame element that content actually gets: clientHeight
    includes the padding the frame reserves, which content cannot use. */
export const innerSize = (element: HTMLElement): Size => ({
    width: element.clientWidth,
    height: element.clientHeight - VIEWER_PADDING * 2,
});
